import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';

type DemoSpeaker = 'rep' | 'prospect' | null;

interface DemoLine {
  speaker: DemoSpeaker;
  name: string;
  text: string;
  pauseAfter: number;
}

@Component({
  selector: 'app-battlecards-demo',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './battlecards-demo.component.html',
  styleUrls: ['./battlecards-demo.component.scss'],
})
export class BattlecardsDemoComponent implements OnDestroy {
  @Input() title = 'What are Battlecards and How do they work?';
  @Input() subtitleItems: string[] = [
    'Upload PDFs like decks, FAQs, security docs, competitive battlecards, or even your own sales notes.',
    'Add public URLs like your website, help center, or docs.company.com - convverse prioritizes answers from these during your calls.',
    'convverse detects prospect questions live during the meeting.',
    'Relevant answers from your PDFs and URLs appear live during the call, helping you avoid saying “Let me get back to you".',
  ];
  @Input() autoStart = true;
  @Input() dismissOnBackdrop = true;

  show = false;
  isRunning = false;
  activeSpeaker: DemoSpeaker = null;
  captionName = 'Live Transcript';
  captionText = 'Waiting for participants to speak...';
  isTyping = false;

  questionVisible = false;
  loadingAnswers = false;
  pdfAnswer = '';
  webAnswer = '';

  private demoToken = 0;
  private readonly typingSpeed = 30;

  readonly meetingScript: DemoLine[] = [
    {
      speaker: 'prospect',
      name: 'Prospect (VP Eng)',
      text: 'Do you support SOC 2 Type II and SSO? What does onboarding look like?',
      pauseAfter: 500,
    },
  ];

  open(): void {
    this.show = true;
    this.resetDemo();
    if (this.autoStart) {
      this.startDemo();
    }
  }

  close(): void {
    this.cancelDemo();
    this.show = false;
  }

  onBackdropClick(): void {
    if (this.dismissOnBackdrop) {
      this.close();
    }
  }

  startDemo(): void {
    if (this.isRunning) return;

    this.resetDemo();
    this.isRunning = true;
    const token = ++this.demoToken;
    void this.runMeeting(token);
  }

  ngOnDestroy(): void {
    this.cancelDemo();
  }

  private async runMeeting(token: number): Promise<void> {
    for (const line of this.meetingScript) {
      if (token !== this.demoToken) return;

      this.setActiveSpeaker(line.speaker);
      this.captionName = line.name;
      await this.typeWriter(line.text, token);
      if (token !== this.demoToken) return;

      await this.wait(line.pauseAfter);
    }

    if (token !== this.demoToken) return;

    this.setActiveSpeaker(null);
    this.showQuestion();
    await this.wait(1200);
    if (token !== this.demoToken) return;

    this.showAnswers();

    await this.wait(1500);
    if (token !== this.demoToken) return;

    this.setActiveSpeaker('rep');
    this.captionName = 'You';
    await this.typeWriter(
      'Yes, we fully support SOC 2 Type II, and SSO is available via Okta, Azure, and Google. Onboarding typically takes about 2 weeks, covering security review and workspace setup.',
      token,
    );

    if (token !== this.demoToken) return;
    await this.wait(1000);
    this.setActiveSpeaker(null);
    this.isRunning = false;

    window.setTimeout(() => {
      if (!this.show || token !== this.demoToken) return;
      this.startDemo();
    }, 2500);
  }

  private resetDemo(): void {
    this.cancelDemo();
    this.activeSpeaker = null;
    this.captionName = 'Live Transcript';
    this.captionText = 'Waiting for participants to speak...';
    this.questionVisible = false;
    this.loadingAnswers = false;
    this.pdfAnswer = '';
    this.webAnswer = '';
    this.isTyping = false;
  }

  private cancelDemo(): void {
    this.demoToken += 1;
    this.isRunning = false;
    this.isTyping = false;
  }

  private async typeWriter(text: string, token: number): Promise<void> {
    this.captionText = '';
    this.isTyping = true;

    for (let i = 0; i < text.length; i += 1) {
      if (token !== this.demoToken) return;
      this.captionText += text.charAt(i);
      await this.wait(this.typingSpeed);
    }

    this.isTyping = false;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  private setActiveSpeaker(speaker: DemoSpeaker): void {
    this.activeSpeaker = speaker;
  }

  private showQuestion(): void {
    this.questionVisible = true;
    this.loadingAnswers = true;
    this.pdfAnswer = '';
    this.webAnswer = '';
  }

  private showAnswers(): void {
    this.loadingAnswers = false;
    this.pdfAnswer =
      'Onboarding takes about 2 weeks and includes security review, workspace setup, and enablement.';
    this.webAnswer =
      'SOC 2 Type II is supported. SSO available via Okta, Azure AD, and Google Workspace.';
  }
}
